import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import MGCompatibilityDisplay from '@/components/MGCompatibilityDisplay';
import { VideoPreview } from '@/components/MGTile/components/VideoPreview';
import { Badge } from '@music-vine/cadence/ui';
import { useMouseRest } from '@/hooks/ui/useMouseRest';
import useTWScreens from '@/hooks/ui/useTWScreens';
import type { MGCompatibility } from '@music-vine/setlist';
interface MGSearchResultCardProps {
  compatibility?: MGCompatibility;
  contributor: string;
  isLut?: boolean;
  isNew?: boolean;
  name: string;
  onClick: (slug: string) => void;
  posterUrl: string;
  previewVideoUrl?: string;
  slug: string;
  variant?: 'default' | 'globalSearch';
}
const VIDEO_HIDE_DELAY_MS = 350;
export const MGSearchResultCard = memo(({
  name,
  contributor,
  posterUrl,
  slug,
  isNew = false,
  isLut = false,
  onClick,
  previewVideoUrl,
  compatibility,
  variant = 'default'
}: MGSearchResultCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const {
    isMd
  } = useTWScreens();
  const {
    handlers
  } = useMouseRest({
    enabled: isHovered && window.matchMedia('(min-width: 768px)').matches,
    hasFocus: isFocused
  });
  const isActive = isHovered && window.matchMedia('(min-width: 768px)').matches || isFocused;
  useEffect(() => {
    if (isActive) {
      setShowVideo(true);
    } else {
      // Clear any existing timeout
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timeout to hide video
      timerRef.current = setTimeout(() => {
        setShowVideo(false);
      }, VIDEO_HIDE_DELAY_MS);
    }

    // Cleanup function to clear timeout on unmount or when isActive changes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isActive]);
  const handleClick = useCallback(() => {
    onClick(slug);
  }, [onClick, slug]);
  const generateAssetUrl = useCallback(() => {
    if (isLut) {
      return `/luts/asset/${slug}`;
    }
    return `/motion-graphics/asset/${slug}`;
  }, [isLut, slug]);
  const assetUrl = generateAssetUrl();

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedBadge = isMd ? motion.div : 'div';
  return <div className="group -m-2 flex flex-col gap-1 rounded-xl p-2 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-950">
        <Link href={assetUrl} onBlur={() => {
      setIsFocused(false);
      setIsHovered(false);
    }} onClick={handleClick} onFocus={() => setIsFocused(true)}>
          {/** biome-ignore lint/a11y/noStaticElementInteractions: This is fine, the real interaction is on the <Link /> */}
          {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: This is fine, the real interaction is on the <Link /> */}
          <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-xl rounded-tr-md font-bold font-sans text-gray-300" onMouseEnter={e => {
        if (window.matchMedia('(min-width: 768px)').matches) {
          setIsHovered(true);
          handlers.onMouseEnter(e);
        }
      }} onMouseLeave={e => {
        if (window.matchMedia('(min-width: 768px)').matches) {
          setIsHovered(false);
          handlers.onMouseLeave(e);
        }
      }} onMouseMove={e => {
        if (window.matchMedia('(min-width: 768px)').matches) {
          handlers.onMouseMove(e);
        }
      }}>
            {previewVideoUrl ? <VideoPreview alt={name} imageUrl={posterUrl} isPlaying={isActive} showVideo={showVideo} src={previewVideoUrl} /> : <Image alt={name} className="h-full w-full rounded-xl bg-gray-50 dark:bg-gray-700" height={720} src={posterUrl} width={1280} />}
            {compatibility && !isLut && <div className="absolute top-2 left-2">
                <MGCompatibilityDisplay className="rounded-lg bg-white/95 px-1.5 py-1 font-normal font-sans text-gray-950" compatibility={compatibility} variant={variant} />
              </div>}
            {isNew && <AnimatedBadge className="absolute top-2 right-2" layout={isMd ? 'position' : undefined}>
                <Badge className="rounded-md font-sans font-semibold uppercase will-change-transform" size="sm" strong variant="contrast">
                  New
                </Badge>
              </AnimatedBadge>}
          </div>

          <p className="mt-3 hidden truncate font-[450] font-sans text-[#0B0C0C] text-base md:block dark:text-gray-50">
            {name}
          </p>
          <p className="text-gray-700 text-xs 2xl:text-sm dark:text-gray-200">
            {contributor}
          </p>
        </Link>
      </div>;
});
MGSearchResultCard.displayName = 'MGSearchResultCard';